import React, { useState, useEffect, useCallback } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { IProps } from './CookieConsentBlock.interface';
import Button from '@/elements/Button/Button.component';
import Text from '@/elements/Text/Text.component';
import clsx from 'clsx';
import './CookieConsentBlock.styles.css';

export const CookieConsentBlock: React.FC<IProps> = ({
    message,
    cookieConsentId,
    expireInDays,
    onAccept,
    onDecline,
}) => {
    const [open, setOpen] = useState(false);
    const [hasConsent, setHasConsent] = useState(true);

    const getCookie = useCallback((name: string): string | null => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
    }, []);

    const setCookie = useCallback(
        (name: string, value: string, days: number) => {
            let expires = '';
            if (days) {
                const date = new Date();
                date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
                expires = `; expires=${date.toUTCString()}`;
            }
            document.cookie = `${name}=${value || ''}${expires}; path=/`;
        },
        [],
    );

    useEffect(() => {
        const consentStatus = getCookie(cookieConsentId);
        if (consentStatus === null) {
            setHasConsent(false);
            setOpen(true);
        }
    }, [cookieConsentId, getCookie]);

    const handleAccept = () => {
        setCookie(cookieConsentId, 'true', expireInDays);
        setOpen(false);
        setHasConsent(true);
        if (onAccept) {
            onAccept();
        }
    };

    const handleDecline = () => {
        setCookie(cookieConsentId, 'false', expireInDays); // Or a shorter duration
        setOpen(false);
        setHasConsent(false); // User has actively declined
        if (onDecline) {
            onDecline();
        }
    };

    if (hasConsent && !open) {
        return null;
    }

    return (
        <Toast.Provider swipeDirection="right">
            <Toast.Root
                className={clsx('cookieConsentBlock')}
                open={open}
                onOpenChange={setOpen}
                duration={Infinity} // Keep open until explicitly closed
            >
                <div className={clsx('cookieConsentBlock__content')}>
                    {message && (
                        <Toast.Description asChild className={clsx('cookieConsentBlock__message')}>
                            <Text html={message} />
                        </Toast.Description>
                    )}
                    <div className={clsx('cookieConsentBlock__actions')}>

                        <Toast.Action asChild altText="Accept cookies">
                            <Button
                                className={clsx('cookieConsentBlock__button', 'cookieConsentBlock__button--accept')}
                                onClick={handleAccept}
                                aria-label="Accept cookies"
                            >
                                Accept
                            </Button>
                        </Toast.Action>
                        <Toast.Action asChild altText="Decline cookies">
                            <Button
                                className={clsx('cookieConsentBlock__button', 'cookieConsentBlock__button--decline')}
                                onClick={handleDecline}
                                aria-label="Decline cookies"
                                variant='secondary'
                            >
                                Decline
                            </Button>
                        </Toast.Action>
                    </div>
                </div>
            </Toast.Root>
            <Toast.Viewport className="fixed bottom-0 right-0 flex flex-col p-6 gap-3 w-full max-w-md m-0 list-none z-[2147483647] outline-none" />
        </Toast.Provider>
    );
};
